import React, { useState } from 'react';
import {
  loadKeysByWeekByCompany,
  exportKeysByWeekByCompany,
} from '../../../../actions/reportByWeekActions';
import { Table, SimpleTextFilter, TableColumn, getVisibleColumns } from '../../../common/Table';
import { CommaNumber_NoDecimal } from '../../../../util/formatter';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../../hooks/useTableSettings';
import YearFormSelect from '../YearFormSelect';
import CompanyLink from '../../../common/links/CompanyLink';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { WEEK_COLUMN_COUNT } from '../../../../util/reportUtils';
import { useReportYear } from '../../../../hooks/useReportYear';
import ReportListLayout from '../ReportListLayout';

export interface KeysByWeekByCompanyListProps {
  tableViewSwitcher?: React.ReactNode;
}

function KeysByWeekByCompanyList({ tableViewSwitcher }: KeysByWeekByCompanyListProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [reload, setReload] = useState<Date | undefined>();
  const [items, setItems] = useState<any>();

  const allColumns = ['companyName', 'year'];
  for (let i = 1; i <= WEEK_COLUMN_COUNT; i++) {
    allColumns.push(`week${i}`);
  }
  const defaultVisibleColumns = allColumns;

  const { year, years, makeHandleYearChange } = useReportYear();
  const params = new URLSearchParams(location.search);
  const {
    tableSettings,
    handleSetTableSettings: setTableSettings,
    filters,
    filterChange,
    isLoading,
    parameters,
  } = useTableSettings(
    'keysbyweekreport-settings',
    getTableSettingsDefaults({
      defaultSort: 'companyName',
      defaultVisibleColumns,
    }),
    { year },
    loadKeysByWeekByCompany,
    setItems,
    reload,
    t('Failed to load report')
  );

  const handleYearChange = makeHandleYearChange(parameters, setReload);

  let columnsConfig: TableColumn[] = [
    {
      Key: 'companyName',
      Label: t('Company'),
      Render: (item) => (
        <CompanyLink
          id={item.companyId}
          name={item.companyName}
          imageSize={32}
        />
      ),
      Filter: {
        Render: (col) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'year',
      Label: t('Year'),
      Render: (item) => item.year,
    },
  ];
  for (let i = 1; i <= WEEK_COLUMN_COUNT; i++) {
    columnsConfig.push({
      Key: `week${i}`,
      Label: i.toString(),
      Render: (row) => CommaNumber_NoDecimal(row[`week${i}`]),
    });
  }

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  const formFilters = (
    <div className="col-md-6">
      <YearFormSelect
        year={year}
        years={years}
        handleYearChange={handleYearChange}
      />
    </div>
  );

  return (
    <ReportListLayout formFilters={formFilters} hasData={!!items}>
      <Table
        className="table-v-stripes"
        columns={columns}
        allColumns={allColumns}
        rows={items.data.map((x: any) => ({ Key: x.companyId, ...x }))}
        totalRowCount={items.totalRowCount}
        filteredRowCount={items.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={(filters, scopes) =>
          exportKeysByWeekByCompany(year, filters, scopes)
        }
        tableActionBarButtons={tableViewSwitcher}
      />
    </ReportListLayout>
  );
}

export default KeysByWeekByCompanyList;
